import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { ICubicle } from "../models/cubicle";
import { ICubicleApiResponse } from "../models/api/cubicleApiResponse";
import { ICubicleFilter } from "../models/filters/cubicleFilter";
import CubicleService from "../services/CubicleService";
import { ICubicleForm } from "../models/forms/cubicleForm";
import { ServiceOperationResult } from "../models/api/serviceOperationResult";
import DataOperationError from "../models/api/dataOperationError";
import { IDataOperationResult } from "../models/api/dataOperationResult";
import { IBoar } from "../models/boar";
import { IBuilding } from "../models/building";
import { ITypeLocation } from "../models/typeLocation";
import SchemaViewModel from "../models/viewModels/schemaViewModel";
import { useBoars } from "./boars.store";
import AnomalieViewModel from "../models/viewModels/anomalieViewModel";
import FilterViewModel from "../models/viewModels/filterViewModel";
import { useAttachmentStore } from "./attachment.store";
import { useMountingPlanStore } from "./mountingPlan.store";
import i18n from "../i18n";
import { useUsersStore } from "./users.store";
import { useIndexStore } from "./indexes.store";
import { useTypeLocationStore } from "./typeLocation.store";
import { useGroupMountingStore } from "./groupMounting.store";
import { useBuildingStore } from "./building.store";
import { useMountingPlanTileDataStore } from "./computedStores/mountingPlanTileData.store";
import signalRManager from "../helper/signalRManager";
import IPayloadStore from "../models/api/payloadStore";
import { StateOfReform } from "../models/reform";

export function isExplicitlyRenamed(c: { customName?: string; corridor: string; number: number }): boolean {
    return !!c.customName && c.customName !== `${c.corridor}.${c.number}`;
}

export const useCubicleStore = defineStore("cubicles", () => {
    // State
    const datas = ref<ICubicle[]>([]);
    const current = ref<ICubicle>({} as ICubicle);
    const itemForm = ref<ICubicleForm>({} as ICubicleForm);
    const arrayForm = ref<ICubicleForm[]>([]);
    const isLoading = ref<boolean>(false);
    const buildingId = ref<number>(0);
    const mountingPlanId = ref<number>(0);
    const groupMountingId = ref<number>(0);
    const filterGrid = ref(new FilterViewModel());
    const selectedSectorIds = ref<number[]>([]);

    // Actions - Hydration
    /**
     * Hydrate Cubicles from API response
     */
    async function hydrateCubicles(cubicles: ICubicleApiResponse[]): Promise<ICubicle[]> {
        if (cubicles.length === 0) return [];

        const userStore = useUsersStore();

        // 1. Get stores
        const buildingStore = useBuildingStore();
        const typeLocationStore = useTypeLocationStore();
        const boarStore = useBoars();

        // 2. Load in parallel if needed
        const loadPromises: Promise<unknown>[] = [];
        if (buildingStore.datas.length === 0) {
            loadPromises.push(buildingStore.load({ page: 1, pageSize: 0 }));
        }
        if (typeLocationStore.datas.length === 0) {
            loadPromises.push(typeLocationStore.load({ page: 1, pageSize: 0 }));
        }
        if (loadPromises.length > 0) {
            await Promise.all(loadPromises);
        }

        // 3. Create Maps for O(1) lookups
        const buildingMap = new Map(buildingStore.datas.map(b => [b.id, b]));
        const typeLocationMap = new Map(typeLocationStore.datas.map(t => [t.id, t]));

        // 4. Hydrate all entities in one pass
        const models: ICubicle[] = [];
        for (const cubicle of cubicles) {
            const model = { ...cubicle } as unknown as ICubicle;

            // Link references (O(1) via Map)
            model.building = buildingMap.get(cubicle.buildingId) ?? model.building;
            model.typeLocation = typeLocationMap.get(cubicle.typeLocationId) ?? model.typeLocation;

            // Boar is optional - hydrate if boarId is set
            if (cubicle.boarId && cubicle.boarId > 0) {
                model.boar = boarStore.getByIdInStore(cubicle.boarId, userStore.currentCenter.id);
            }

            models.push(model);
        }

        return models;
    }

    // Getters
    const getSchemaList = computed((): SchemaViewModel[] => {
        const tileDataStore = useMountingPlanTileDataStore();
        const mountingPlanStore = useMountingPlanStore();
        const attachmentStore = useAttachmentStore();
        const groupStore = useGroupMountingStore();

        // Use mountingPlanId instead of mountingPlan.id to avoid undefined access
        const mountingPlanGroupIds = new Set(groupStore.datas.filter(g => g.mountingPlanId == mountingPlanId.value).map(g => g.id));
        if (mountingPlanGroupIds.size == 0) {
            return [];
        }

        const mountingPlanAttachments = attachmentStore.datas.filter(a => mountingPlanGroupIds.has(a.groupMountingId));

        const allAttachedBoarIds = new Set(mountingPlanAttachments.map(a => a.boarId));

        const bldId = mountingPlanStore.buildingId;
        const COMM_CODES = new Set(['1', '2', '3']);
        const agg = new Map<number, SchemaViewModel>();

        for (const cubicle of datas.value) {
            const boar = cubicle.boar;
            if (!boar) continue;
            if (bldId !== 0 && cubicle.buildingId !== bldId) continue;

            const gt = boar.geneticType;
            if (!gt) continue;

            const id = gt.id;
            const name = gt.code3 ?? '';
            const isCommercial = COMM_CODES.has(boar.conservationAptitude?.code3 ?? '') && boar.productionStatus?.code3 == 'P';
            const isArmada = boar.armada ? 1 : 0;

            let nbrDoses = 0;
            let nbrSelect = 0;
            const spzAverage = tileDataStore.spzAverageByBoarId.get(boar.id) ?? 0;

            if (allAttachedBoarIds.has(boar.id)) {
                nbrSelect = 1;
                nbrDoses = spzAverage;
            }

            let vm = agg.get(id);
            if (!vm) {
                vm = new SchemaViewModel(
                    id,
                    name,
                    nbrSelect,
                    nbrDoses,
                    1,
                    isCommercial ? 1 : 0,
                    isCommercial ? spzAverage : 0,
                    isArmada ? 1 : 0
                );
                agg.set(id, vm);
            } else {
                vm.nbrTotalAnimals += 1;
                vm.nbrSelect += nbrSelect;
                vm.nbrDose += nbrDoses;
                if (isCommercial) {
                    vm.nbrTotalCommercial += 1;
                    vm.nbrTotalDoseCommercial += spzAverage;
                }
                if (isArmada) vm.nbrArmada += 1;
            }
        }

        return [...agg.values()];
    });

    /**
     * Nombre de verrats réellement placés dans un bâtiment, par type génétique (schéma). Alimente le
     * bandeau PARC du tableau des compteurs (Mantis 0028608).
     *
     * Même source que le plan de monte (`getSchemaList` : `cubicle.boar`, hydraté par `boarId`), et
     * non les champs barn/corridor/box du verrat que lit `getAnomalies`. Ne comptent donc PAS : un
     * verrat sans box, un verrat dont l'emplacement n'existe pas dans le bâtiment, ni un verrat
     * placé dans un box dont le type n'autorise pas les verrats (anomalie « Medium »). Un verrat
     * n'est compté qu'une fois, même s'il apparaît dans plusieurs boxes. Réformés et sortis exclus :
     * ils ont quitté le parc.
     */
    const getNbPlacedByGeneticType = computed(() => (bldId: number) => {
        const counts = new Map<number, number>();
        const seenBoarIds = new Set<number>();
        for (const cubicle of datas.value) {
            if (!cubicle.active || cubicle.buildingId !== bldId) continue;
            const boar = cubicle.boar;
            if (!boar || seenBoarIds.has(boar.id)) continue;
            if (cubicle.typeLocation && !cubicle.typeLocation.isBoarAllowed) continue;
            if (boar.exitDate || boar.reform?.state === StateOfReform.Validate) continue;
            const geneticTypeId = boar.geneticType?.id;
            if (geneticTypeId == null) continue;
            seenBoarIds.add(boar.id);
            counts.set(geneticTypeId, (counts.get(geneticTypeId) ?? 0) + 1);
        }
        return counts;
    });

    const getAnomalies = computed((): AnomalieViewModel[] => {
        const boarStore = useBoars();
        const userStore = useUsersStore();
        const buildingStore = useBuildingStore();

        const centerId = userStore.currentCenter.id;
        const activeCubicles = datas.value.filter(c => c.active);

        let selectedBuildingNumber: number | null = null;
        if (buildingId.value > 0) {
            const building = buildingStore.datas.find(b => b.id === buildingId.value);
            if (building) {
                selectedBuildingNumber = building.number;
            }
        }

        // Create building map for O(1) lookups
        const buildingMap = new Map(buildingStore.datas.map(b => [b.id, b]));

        const cubicleByLocation = new Map<string, ICubicle>(
            (activeCubicles as ICubicle[]).map(c => {
                const building = buildingMap.get(c.buildingId);
                const buildingNumber = building?.number ?? 0;
                const key = c.customName ?? `${c.corridor}.${c.number}`;
                return [`${buildingNumber}-${key}`, c];
            })
        );

        const activeBoars = boarStore.datas.filter(
            b => b.stud.id === centerId && !b.exitDate && (selectedBuildingNumber === null || b.barn === selectedBuildingNumber || b.barn == 0)
        );

        const boarsByLocation = new Map<string, IBoar[]>();
        for (const boar of activeBoars) {
            if (boar.barn && boar.corridor && boar.box) {
                const key = `${boar.barn}-${boar.corridor}.${boar.box}`;
                const list = boarsByLocation.get(key) ?? [];
                list.push(boar);
                boarsByLocation.set(key, list);
            }
        }

        const formatLocation = (b: IBoar) =>
            `(${b.barn ?? "?"}-${b.corridor ?? "?"}-${b.box ?? "?"})`;

        const createAnomaly = (
            boar: IBoar,
            nameKey: string,
            logMessage: string,
            severity: string = "High"
        ) => new AnomalieViewModel(
            boar.id,
            i18n.global.t(nameKey),
            formatLocation(boar),
            logMessage,
            severity,
            boar
        );

        const anomalies: AnomalieViewModel[] = [];
        const processedMultipleBoars = new Set<number>();

        for (const boar of activeBoars) {
            const hasNoCorridor = !boar.corridor || boar.corridor === '0';

            if (hasNoCorridor) {
                anomalies.push(createAnomaly(
                    boar,
                    'cubicle.anomalies.boarWithoutCubicle',
                    i18n.global.t('cubicle.anomalies.log.invalidCoordinates', { name: boar.name, id: boar.id })
                ));
                continue;
            }

            const locationKey = `${boar.barn}-${boar.corridor}.${boar.box}`;
            const boarsAtLocation = boarsByLocation.get(locationKey) ?? [];
            const cubicle = cubicleByLocation.get(locationKey);

            if (boarsAtLocation.length > 1 && !processedMultipleBoars.has(boar.id)) {
                for (const b of boarsAtLocation) {
                    if (!processedMultipleBoars.has(b.id)) {
                        processedMultipleBoars.add(b.id);
                        anomalies.push(createAnomaly(
                            b,
                            'cubicle.anomalies.multipleBoarsInCubicle',
                            i18n.global.t('cubicle.anomalies.log.sharedLocation', { name: b.name, id: b.id })
                        ));
                    }
                }
                continue;
            }

            if (!cubicle) {
                anomalies.push(createAnomaly(
                    boar,
                    'cubicle.anomalies.boarWithInvalidCubicle',
                    i18n.global.t('cubicle.anomalies.log.locationNotExists', { name: boar.name, id: boar.id, location: locationKey })
                ));
                continue;
            }

            if (cubicle.typeLocation && !cubicle.typeLocation.isBoarAllowed) {
                anomalies.push(createAnomaly(
                    boar,
                    'cubicle.anomalies.boarWithInvalidCubicle',
                    i18n.global.t('cubicle.anomalies.log.boxNotAllowed', { name: boar.name, id: boar.id }),
                    "Medium"
                ));
            }
        }

        return anomalies;
    });

    /**
     * Optimized filter returning a Set for O(1) membership checks
     * Use this in components: cubicleStore.getByFilterSet.has(cubicleId)
     */
    const getByFilterSet = computed((): Set<number> => {
        return new Set(getByFilter.value);
    });

    /**
     * Optimized filter with Maps for O(1) lookups instead of O(n) filter/find
     * Reduces complexity from O(n³) to O(n)
     */
    const getByFilter = computed((): number[] => {
        if (filterGrid.value.isEmpty()) return [];

        const indexedStore = useIndexStore();
        const tileDataStore = useMountingPlanTileDataStore();
        const attachmentStore = useAttachmentStore();

        // Build Maps once for O(1) lookups (instead of O(n) filter/find per cubicle)
        const attachmentsByBoarId = new Map<number, IAttachment[]>();
        for (const att of attachmentStore.datas) {
            if (!att.boarId) continue;
            let list = attachmentsByBoarId.get(att.boarId);
            if (!list) { list = []; attachmentsByBoarId.set(att.boarId, list); }
            list.push(att);
        }

        const indexesByAnimalId = new Map<number, IIndex[]>();
        for (const idx of indexedStore.datas) {
            // Use animalId instead of animal?.id to avoid undefined access
            if (!idx.animalId) continue;
            let list = indexesByAnimalId.get(idx.animalId);
            if (!list) { list = []; indexesByAnimalId.set(idx.animalId, list); }
            list.push(idx);
        }

        const currentCubicles = (getForBuilding.value as ICubicle[]).filter(c => c.boarId);

        const rIds = currentCubicles.filter(c => {
            const boarId = c.boarId!;
            const boar = c.boar;
            if (!boar) return false;

            const nameMatch = filterGrid.value.search.length == 0 || (
                boar.name.toLowerCase().includes(filterGrid.value.search.toLowerCase()) ||
                (boar.workNumber ?? "").toLowerCase().includes(filterGrid.value.search.toLowerCase()) ||
                (boar.studInfo ?? "").toLowerCase().includes(filterGrid.value.search.toLowerCase())
            );

            const aptitudeCode = boar.conservationAptitude?.code3 ?? "";
            const aptMatch = filterGrid.value.aptitudes.length == 0 || (
                filterGrid.value.aptitudes.includes(aptitudeCode) ||
                // Le filtre "4" inclut aussi les animaux sans aptitude (vide ou "?")
                (filterGrid.value.aptitudes.includes("4") && (aptitudeCode === "" || aptitudeCode === "?"))
            );
            const armadaMatch = filterGrid.value.isArmada ? boar.armada : true;
            const nnMatch = filterGrid.value.isNN ? (boar.nn?.code3 == 'NN') : true;

            const tileData = tileDataStore.tileDataByBoarId.get(boarId);
            const premiumMatch = filterGrid.value.isPremium ? (tileData?.premium ?? false) : true;

            // O(1) Map lookup instead of O(n) filter
            let isGroupBoar = true;
            if (filterGrid.value.groupId > 0) {
                const boarAttachments = attachmentsByBoarId.get(boarId) ?? [];
                // Use groupMountingId instead of groupMounting?.id to avoid undefined access
                isGroupBoar = boarAttachments.some(g => g.groupMountingId == filterGrid.value.groupId);
            }

            // O(1) Map lookup + O(k) find (k = indexes per animal, typically 1-2)
            // instead of O(n) find on all indexes
            const boarIndexes = indexesByAnimalId.get(boarId) ?? [];
            const indexTest = boarIndexes.find(i => i.origin == 'TESTAGE')?.vgGlobale ?? 0;
            const indexIfip = boarIndexes.find(i => i.origin == '420VGB')?.vgGlobale ?? 0;
            const index = indexIfip > 0 ? indexIfip : indexTest;
            const indexedMatch = filterGrid.value.isIndexed ? index >= filterGrid.value.minIndex && index <= filterGrid.value.maxIndex : true;

            const schemaMatch = filterGrid.value.schemaId == 0 || (filterGrid.value.schemaId > 0 && boar.geneticType != null && filterGrid.value.schemaId == boar.geneticType?.id);

            return nameMatch && aptMatch && armadaMatch && nnMatch && schemaMatch && isGroupBoar && indexedMatch && premiumMatch;
        }).map(c => c.id);

        return rIds;
    });

    const getForBuilding = computed((): ICubicle[] => {
        return datas.value.filter(c => c.buildingId == buildingId.value && c.active);
    });

    const getByBuildingId = computed(() => (bldId: number) => {
        return datas.value.filter(c => c.buildingId == bldId && c.active);
    });

    const getEditionColumnsX = computed(() => {
        const locationTypeStore = useTypeLocationStore();
        const notAllowedIds = locationTypeStore.getIdsWhereBoarsNotAllowed;

        const s = new Set(arrayForm.value.filter(c => !isExplicitlyRenamed(c) && !notAllowedIds.has(c.typeLocationId)).map(c => c.column));
        return [...s].sort((a, b) => a - b);
    });

    const getEditionRowsY = computed(() => {
        const locationTypeStore = useTypeLocationStore();
        const notAllowedIds = locationTypeStore.getIdsWhereBoarsNotAllowed;

        const s = new Set(arrayForm.value.filter(c => !isExplicitlyRenamed(c) && !notAllowedIds.has(c.typeLocationId)).map(c => c.row));
        return [...s].sort((a, b) => a - b);
    });

    const getDisplayColumnsX = computed(() => {
        const locationTypeStore = useTypeLocationStore();
        const notAllowedIds = locationTypeStore.getIdsWhereBoarsNotAllowed;

        const buildingCubicles = getForBuilding.value as ICubicle[];
        const s = new Set(buildingCubicles
            .filter(c => !notAllowedIds.has(c.typeLocationId))
            .map(c => c.column)
        );
        return [...s].sort((a, b) => a - b);
    });

    const getDisplayColumnsXForBuilding = computed(() => (bldId: number) => {
        const locationTypeStore = useTypeLocationStore();
        const notAllowedIds = locationTypeStore.getIdsWhereBoarsNotAllowed;

        const cubicles = datas.value.filter(c => c.buildingId == bldId && c.active);

        const s = new Set(cubicles
            .filter(c => !notAllowedIds.has(c.typeLocationId))
            .map(c => c.column)
        );
        return [...s].sort((a, b) => a - b);
    });

    const getDisplayRowsY = computed(() => {
        const locationTypeStore = useTypeLocationStore();
        const notAllowedIds = locationTypeStore.getIdsWhereBoarsNotAllowed;

        const buildingCubicles = getForBuilding.value as ICubicle[];
        const s = new Set(buildingCubicles
            .filter(c => !notAllowedIds.has(c.typeLocationId))
            .map(c => c.row)
        );
        return [...s].sort((a, b) => a - b);
    });

    const getDisplayRowsYForBuilding = computed(() => (bldId: number) => {
        const locationTypeStore = useTypeLocationStore();
        const notAllowedIds = locationTypeStore.getIdsWhereBoarsNotAllowed;

        const cubicles = datas.value.filter(c => c.buildingId == bldId && c.active);

        const s = new Set(cubicles
            .filter(c => !notAllowedIds.has(c.typeLocationId))
            .map(c => c.row)
        );
        return [...s].sort((a, b) => a - b);
    });

    // Actions
    async function load(filter: ICubicleFilter) {
        isLoading.value = true;
        let result = await CubicleService.getAll<ICubicleFilter, ICubicleApiResponse>(filter);
        isLoading.value = false;
        datas.value = [];
        if (!result.error) {
            datas.value = await hydrateCubicles(result.data as ICubicleApiResponse[]);
        }
        return result;
    }

    async function get(id: number) {
        let result = await CubicleService.getById<ICubicleApiResponse>(id);
        if (!result.error) {
            const [modelData] = await hydrateCubicles([result.data as ICubicleApiResponse]);

            let index = datas.value.findIndex((a) => a.id == id);
            if (index >= 0)
                datas.value[index] = modelData;

            current.value = modelData;
            itemForm.value = toForm(modelData);
        }
        return result;
    }

    async function searchStoreById(id: number) {
        current.value = datas.value.find((objet) => objet.id == id)!;
        itemForm.value = toForm(current.value);
        return current.value;
    }

    async function save(maj: boolean = true, isPatch: boolean = false) {
        let result = {} as ServiceOperationResult<ICubicle | IDataOperationResult<ICubicle>>;
        if (itemForm.value.id > 0) {
            result = await CubicleService.update(itemForm.value);

            if (!result.error) {
                const [modelData] = await hydrateCubicles([result.data as ICubicleApiResponse]);
                current.value = modelData;

                if (maj) {
                    let index = datas.value.findIndex((a) => a.id == itemForm.value.id);
                    if (index >= 0) {
                        datas.value[index] = current.value;
                    } else {
                        const error = new DataOperationError();
                        error.REQUEST_ERROR = i18n.global.t("errors.itemNotFoundInStore");
                        return new ServiceOperationResult<IDataOperationResult<ICubicle>>(true, { errors: error });
                    }
                }
            }
        } else {
            result = await CubicleService.create(itemForm.value);
            if (!result.error) {
                const [modelData] = await hydrateCubicles([result.data as ICubicleApiResponse]);
                current.value = modelData;
            }
        }
        return result;
    }

    async function batchSave(formDatas: ICubicleForm[], maj: boolean = true) {
        const boarStore = useBoars();

        isLoading.value = true;

        const returnItems: ICubicle[] = [];
        const createItems = formDatas.filter(d => d.id == 0) as ICubicleForm[];
        const updateItems = formDatas.filter(d => d.id > 0) as ICubicleForm[];
        if (createItems.length > 0) {
            let result = await CubicleService.batchCreate(createItems);
            if (!result.error && maj) {
                const hydratedData = await hydrateCubicles(result.data as ICubicleApiResponse[]);
                for (const c of hydratedData) {
                    datas.value.push(c);
                    returnItems.push(c);
                }
            }
        }
        if (updateItems.length > 0) {
            let result = await CubicleService.batchUpdate(updateItems);
            if (!result.error && maj) {
                const hydratedData = await hydrateCubicles(result.data as ICubicleApiResponse[]);
                for (const c of hydratedData) {
                    let index = datas.value.findIndex((a) => a.id == c.id);
                    if (index >= 0) {
                        datas.value[index] = c;
                        returnItems.push(c);
                    } else {
                        const error = new DataOperationError();
                        error.REQUEST_ERROR = i18n.global.t("errors.itemNotFoundInStore");
                        return new ServiceOperationResult<IDataOperationResult<ICubicle>>(true, { errors: error });
                    }
                }
            }
        }
        isLoading.value = false;
        return returnItems;
    }

    async function deleteAction() {
        let result = await CubicleService.delete<ICubicleApiResponse>(current.value.id);

        if (!result.error) {
            let index = datas.value.findIndex((a) => a.id == current.value.id);
            if (index >= 0) {
                datas.value.splice(index, 1);
            }

            current.value = newItem();
            itemForm.value = newForm();
        }
        return result;
    }

    async function batchDelete(ids: number[]) {
        isLoading.value = true;
        const result = await CubicleService.batchDelete(ids);
        if (!result.error) {
            datas.value = datas.value.filter(item => !ids.includes(item.id));
        }
        isLoading.value = false;
        return result;
    }

    function toForm(item: ICubicle): ICubicleForm {
        const form = {} as ICubicleForm;

        form.id = item.id;
        form.column = item.column;
        form.corridor = item.corridor;
        form.number = item.number;
        form.row = item.row;
        form.customName = item.customName;

        // Use ID properties directly instead of navigation properties
        form.buildingId = item.buildingId;
        form.typeLocationId = item.typeLocationId;
        form.boarId = item.boarId ?? 0;

        itemForm.value = form;
        return form;
    }

    function newForm(): ICubicleForm {
        itemForm.value = {
            id: 0,
            boarId: 0,
            buildingId: 0,
            column: 0,
            corridor: "",
            number: 0,
            row: 0,
            typeLocationId: 0,
            customName: undefined
        } as ICubicleForm;
        return itemForm.value;
    }

    function newItem(): ICubicle {
        current.value = {
            id: 0,
            boar: { id: 0 } as IBoar,
            building: { id: 0 } as IBuilding,
            typeLocation: { id: 0 } as ITypeLocation,
            corridor: "",
            column: 0,
            row: 0,
            number: 0,
            customName: undefined
        } as ICubicle;

        itemForm.value = toForm(current.value);
        return current.value;
    }

    function removeFromFormAtIndex(i: number) {
        arrayForm.value.splice(i, 1);
    }

    return {
        // State
        datas,
        current,
        itemForm,
        arrayForm,
        isLoading,
        buildingId,
        mountingPlanId,
        groupMountingId,
        filterGrid,
        selectedSectorIds,
        // Getters
        getSchemaList,
        getNbPlacedByGeneticType,
        getAnomalies,
        getByFilter,
        getByFilterSet,
        getForBuilding,
        getByBuildingId,
        getEditionColumnsX,
        getEditionRowsY,
        getDisplayColumnsX,
        getDisplayColumnsXForBuilding,
        getDisplayRowsY,
        getDisplayRowsYForBuilding,
        // Actions
        load,
        get,
        searchStoreById,
        save,
        batchSave,
        delete: deleteAction,
        batchDelete,
        toForm,
        newForm,
        newItem,
        removeFromFormAtIndex,
        hydrateCubicles
    };
});

signalRManager.connection.on("MajCubicle", async (payload: IPayloadStore) => {
    const store = useCubicleStore();
    switch (payload.action) {
        case "Create": {
            if (store.datas.findIndex((a) => a.id == payload.data?.id) == -1) {
                const [hydrated] = await store.hydrateCubicles([payload.data as ICubicleApiResponse]);
                store.datas.push(hydrated);
            }
            break;
        }
        case "Update": {
            const idx = store.datas.findIndex((a) => a.id == payload.data?.id);
            if (idx > -1) {
                const [hydrated] = await store.hydrateCubicles([payload.data as ICubicleApiResponse]);
                store.datas.splice(idx, 1, hydrated);
            }
            break;
        }
        case "Delete": {
            const idx = store.datas.findIndex((a) => a.id == payload.id);
            if (idx > -1) store.datas.splice(idx, 1);
            break;
        }
    }
});
